/**
 * BulkActionBar — Sprint 4.1.1 + Sprint 4.1.2.
 *
 * Barra flutuante exibida no rodapé quando há 1+ device(s) selecionado(s).
 * Sprint 4.1.2 adicionou:
 *   - Stats por status (Online/Offline/Weak) na própria barra
 *   - "Reiniciar Relé" abre BulkRelayRebootModal (mesmo do Device Details)
 *   - "Grupo…" usa bulk-group (era loop client-side)
 *   - "Tag…" usa bulk-tag com action=add/remove (era loop client-side)
 *   - "Exportar" gera CSV client-side
 */
import { useMemo, useState } from "react";

import {
  bulkCommand,
  bulkGroup,
  bulkOta,
  bulkTag,
  listConfigTemplates,
  listFirmwareReleases,
} from "../../lib/api.js";
import BulkRelayRebootModal from "./BulkRelayRebootModal.jsx";
import TemplateApplyModal from "../templates/TemplateApplyModal.jsx";
import { useCriticalConfirm } from "../ConfirmCriticalAction.jsx";

// S3.10A — comandos em massa que derrubam serviço → exigem confirmação forte.
const BULK_CRITICAL = new Set(["relay_off", "relay_reboot", "reboot"]);

function Btn({ children, onClick, disabled, color = "emerald", title }) {
  const palette = {
    emerald: "border-noc-ok/30 text-noc-ok hover:border-noc-ok/30",
    amber: "border-noc-warn/30 text-noc-warn hover:border-noc-warn/30",
    red: "border-noc-err/30 text-noc-err hover:border-noc-err/30",
    blue: "border-noc-accent/30 text-noc-accent hover:border-noc-accent/30",
    purple: "border-noc-purple/30 text-noc-purple hover:border-noc-purple/30",
    cyan: "border-noc-accent/30 text-noc-accent hover:border-noc-accent/30",
    gray: "border-noc-border text-noc-muted hover:text-noc-text",
  }[color] || "border-noc-border text-noc-muted";
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={title}
      className={`text-[11px] px-2.5 py-1 rounded border ${palette} disabled:opacity-40 disabled:cursor-not-allowed`}
    >
      {children}
    </button>
  );
}

function ResultSummary({ summary }) {
  if (!summary) return null;
  const ok = summary.enqueued ?? summary.ok ?? 0;
  const failed = summary.failed ?? 0;
  const noop = summary.noop ?? 0;
  // MAP-MANAGE-1C — protegidos pulados vêm DENTRO de `failed` (backend);
  // separa p/ clareza e evita double-count.
  const skipped = summary.skipped_protected ?? 0;
  const realFailed = Math.max(0, failed - skipped);
  return (
    <div className="text-[10px] text-noc-muted whitespace-nowrap">
      <span className="text-noc-ok">{ok} OK</span>
      {noop > 0 && <> · <span className="text-noc-muted">{noop} noop</span></>}
      {skipped > 0 && (
        <> · <span className="text-noc-warn" title={(summary.protected_skipped_ids || []).join(", ")}>
          🔒 {skipped} protegido(s) pulado(s)
        </span></>
      )}
      {realFailed > 0 && <> · <span className="text-noc-err">{realFailed} falha</span></>}
    </div>
  );
}

/** Sprint 4.1.2 — gera CSV das linhas selecionadas. */
function exportCsv(devicesById, ids) {
  const rows = ids.map((id) => devicesById[id]).filter(Boolean);
  const header = [
    "device_id", "customer_code", "customer_name", "group_name",
    "fw_version", "online", "last_seen", "rssi", "weak_signal",
    "relay", "ota_state", "location_name", "address", "lat", "lng",
    "tags",
  ];
  const escape = (v) => {
    if (v === null || v === undefined) return "";
    const s = String(v);
    if (/[",\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
    return s;
  };
  const lines = [header.join(",")];
  for (const r of rows) {
    const tagsStr = (r.tags || []).map((t) => t.name).join("|");
    lines.push([
      r.device_id, r.customer_code, r.customer_name, r.group_name,
      r.fw_version, r.online, r.last_seen, r.rssi, r.weak_signal,
      r.relay, r.ota_state, r.location_name, r.address, r.lat, r.lng,
      tagsStr,
    ].map(escape).join(","));
  }
  const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `fleet-map-selection-${new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-")}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export default function BulkActionBar({
  selectedIds,           // Set<string>
  onClear,
  customers,
  groups,
  tags,
  devices,               // Sprint 4.1.2 — passa lista completa p/ stats e export
  onAfterAction,
}) {
  const [busy, setBusy] = useState(false);
  const [summary, setSummary] = useState(null);
  const [error, setError] = useState(null);
  const [showOtaPicker, setShowOtaPicker] = useState(false);
  const [showGroupPicker, setShowGroupPicker] = useState(false);
  const [showTagPicker, setShowTagPicker] = useState(false);
  const [showRelayRebootModal, setShowRelayRebootModal] = useState(false);
  const [releases, setReleases] = useState([]);
  // Sprint 4.6 — Template apply
  const [showTemplatePicker, setShowTemplatePicker] = useState(false);
  const [templatesList, setTemplatesList] = useState([]);
  const [applyTemplate, setApplyTemplate] = useState(null);
  const confirmCritical = useCriticalConfirm(); // S3.10A

  // Sprint 4.1.2 — stats e mapa de devices para export
  const devicesById = useMemo(() => {
    const m = {};
    for (const d of devices || []) m[d.device_id] = d;
    return m;
  }, [devices]);

  const stats = useMemo(() => {
    let online = 0, offline = 0, weak = 0;
    for (const id of selectedIds) {
      const d = devicesById[id];
      if (!d) continue;
      if (!d.online) offline += 1;
      else if (d.weak_signal) weak += 1;
      else online += 1;
    }
    return { online, offline, weak };
  }, [selectedIds, devicesById]);

  // MAP-MANAGE-1C — recorte de proteção da seleção. `ota_protected` vem do
  // DTO do fleet-map/devices (MAP-DATA-1B); backend é a fonte da verdade.
  const sel = useMemo(() => {
    let protectedCount = 0;
    let offlineCount = 0; // MAP-UX-1E — offline ENTRE os elegíveis (fica enfileirado)
    const protectedIds = [];
    for (const id of selectedIds) {
      const d = devicesById[id];
      if (d?.ota_protected) {
        protectedCount += 1;
        if (protectedIds.length < 50) protectedIds.push(id);
      } else if (d && !d.online) {
        offlineCount += 1;
      }
    }
    const total = selectedIds.size;
    return {
      total,
      protectedCount,
      eligible: total - protectedCount,
      protectedIds,
      offlineCount,
    };
  }, [selectedIds, devicesById]);

  if (selectedIds.size === 0) return null;
  const ids = Array.from(selectedIds);

  const runBulkCommand = async (type, payload = {}) => {
    // MAP-MANAGE-1C — se TODOS os selecionados são protegidos, bloqueia o submit
    // p/ tipos mutantes (ping não é protegido) — evita falsa sensação de ação.
    if (type !== "ping" && sel.protectedCount > 0 && sel.eligible === 0) {
      setError(
        `Todos os ${sel.total} device(s) selecionados são protegidos (produção)`
        + ` — nenhum receberá '${type}'.`,
      );
      setSummary(null);
      return;
    }
    // S3.10A — crítico (relay_off/reboot) → modal forte + senha; relay_on → simples.
    let confirm;
    if (BULK_CRITICAL.has(type)) {
      const secret = await confirmCritical({
        actionLabel: `${type} em massa`,
        // MAP-UX-1E — breakdown dentro do modal crítico (total/elegíveis/
        // protegidos/offline). Backend continua a fonte da verdade.
        target: {
          kind: "bulk",
          count: ids.length,
          names: ids,
          eligible: sel.eligible,
          protectedCount: sel.protectedCount,
          protectedIds: sel.protectedIds,
          offlineCount: sel.offlineCount,
        },
        impact: "Cada dispositivo recebe o comando no próximo heartbeat.",
      });
      if (secret == null) return;
      confirm = secret;
    } else if (!window.confirm(`Confirma ${type} em ${ids.length} device(s)?`)) {
      return;
    }
    setBusy(true);
    setError(null);
    setSummary(null);
    try {
      const r = await bulkCommand(ids, type, { payload, confirm });
      setSummary(r);
      if (onAfterAction) onAfterAction();
    } catch (e) {
      setError(e?.message || `Erro em bulk ${type}`);
    } finally {
      setBusy(false);
    }
  };

  const openOtaPicker = async () => {
    setBusy(true);
    setError(null);
    try {
      const r = await listFirmwareReleases({ limit: 50 });
      setReleases(r.releases || r || []);
      setShowOtaPicker(true);
    } catch (e) {
      setError(e?.message || "Erro ao listar releases");
    } finally {
      setBusy(false);
    }
  };

  const runBulkOta = async (releaseId) => {
    const secret = await confirmCritical({
      actionLabel: "OTA em massa (atualizar firmware)",
      // MAP-UX-1E — `ota_protected` É a denylist de OTA: o breakdown reflete
      // exatamente quais devices o backend vai pular.
      target: {
        kind: "bulk",
        count: ids.length,
        names: ids,
        eligible: sel.eligible,
        protectedCount: sel.protectedCount,
        protectedIds: sel.protectedIds,
        offlineCount: sel.offlineCount,
      },
      impact: "Vai FLASHAR firmware em vários devices (os sem secure_channel são pulados).",
    });
    if (secret == null) return;
    setShowOtaPicker(false);
    setBusy(true);
    setError(null);
    setSummary(null);
    try {
      const r = await bulkOta(ids, releaseId, { confirm: secret });
      setSummary(r);
      if (onAfterAction) onAfterAction();
    } catch (e) {
      setError(e?.message || "Erro em bulk OTA");
    } finally {
      setBusy(false);
    }
  };

  // Sprint 4.1.2: bulk-group endpoint
  const runBulkGroup = async (groupId) => {
    if (!window.confirm(
      groupId
        ? `Vincular ${ids.length} device(s) ao grupo selecionado?`
        : `Desvincular ${ids.length} device(s) de qualquer grupo?`,
    )) return;
    setShowGroupPicker(false);
    setBusy(true);
    setError(null);
    setSummary(null);
    try {
      const r = await bulkGroup(ids, groupId);
      setSummary(r);
      if (onAfterAction) onAfterAction();
    } catch (e) {
      setError(e?.message || "Erro em bulk group");
    } finally {
      setBusy(false);
    }
  };

  // Sprint 4.1.2: bulk-tag endpoint, action=add
  const runBulkAddTag = async (tagId) => {
    if (!window.confirm(`Adicionar tag selecionada a ${ids.length} device(s)?`)) return;
    setShowTagPicker(false);
    setBusy(true);
    setError(null);
    setSummary(null);
    try {
      const r = await bulkTag(ids, tagId, "add");
      setSummary(r);
      if (onAfterAction) onAfterAction();
    } catch (e) {
      setError(e?.message || "Erro em bulk tag");
    } finally {
      setBusy(false);
    }
  };

  const handleExport = () => {
    try {
      exportCsv(devicesById, ids);
    } catch (e) {
      setError(e?.message || "Erro ao exportar CSV");
    }
  };

  // Sprint 4.6 — abre picker de template
  const openTemplatePicker = async () => {
    setBusy(true);
    setError(null);
    try {
      const r = await listConfigTemplates({ enabledOnly: true });
      setTemplatesList(r.templates || []);
      setShowTemplatePicker(true);
    } catch (e) {
      setError(e?.message || "Erro ao listar templates");
    } finally {
      setBusy(false);
    }
  };

  const onPickTemplate = (tpl) => {
    setShowTemplatePicker(false);
    setApplyTemplate(tpl);
  };

  return (
    <div className="fixed bottom-3 left-1/2 -translate-x-1/2 z-[1000] max-w-[97vw]">
      <div className="bg-noc-panel border border-noc-ok/30 rounded shadow-xl px-3 py-2 flex items-center gap-2 flex-wrap text-xs">
        <div className="font-mono font-bold text-noc-ok px-2 py-0.5 bg-noc-ok/10 rounded">
          {selectedIds.size} selecionado{selectedIds.size > 1 ? "s" : ""}
        </div>

        {/* Sprint 4.1.2: stats por status na própria barra */}
        <div className="text-[10px] text-noc-muted flex items-center gap-2">
          <span title="online" className="text-noc-ok">●{stats.online}</span>
          <span title="weak signal" className="text-noc-warn">●{stats.weak}</span>
          <span title="offline" className="text-noc-err">●{stats.offline}</span>
        </div>

        {/* MAP-MANAGE-1C — preview: protegidos serão ignorados em comandos
            críticos (relay/reboot/config/OTA); ping não é protegido. */}
        {sel.protectedCount > 0 && (
          <div
            className="text-[10px] px-2 py-0.5 rounded bg-noc-warn/10 text-noc-warn border border-noc-warn/30 whitespace-nowrap"
            title={`Protegidos (produção) ignorados pelo backend em comandos críticos: ${sel.protectedIds.join(", ")}`}
          >
            🔒 {sel.protectedCount} ignorado{sel.protectedCount > 1 ? "s" : ""} · {sel.eligible} elegíve{sel.eligible === 1 ? "l" : "is"}
          </div>
        )}

        <Btn onClick={() => runBulkCommand("relay_on")} disabled={busy} color="emerald">▶ Relé ON</Btn>
        <Btn onClick={() => runBulkCommand("relay_off")} disabled={busy} color="amber">■ Relé OFF</Btn>
        <Btn onClick={() => setShowRelayRebootModal(true)} disabled={busy} color="red" title="Reiniciar Relé (com duração)">
          ⟲ Reiniciar Relé
        </Btn>
        <Btn onClick={() => runBulkCommand("reboot")} disabled={busy} color="red" title="Reboot do ESP32">
          ⟳ Reiniciar ESP32
        </Btn>
        <Btn onClick={openOtaPicker} disabled={busy} color="blue">⬇ OTA…</Btn>
        <Btn onClick={() => setShowGroupPicker((v) => !v)} disabled={busy} color="purple">
          ⊞ Grupo…
        </Btn>
        <Btn onClick={() => setShowTagPicker((v) => !v)} disabled={busy} color="purple">
          ⊕ Tag…
        </Btn>
        {/* Sprint 4.1.2 — Exportar */}
        <Btn onClick={handleExport} disabled={busy} color="cyan" title="Exportar CSV">
          ⤓ Exportar
        </Btn>

        {/* Sprint 4.6 — Aplicar Template */}
        <Btn onClick={openTemplatePicker} disabled={busy} color="cyan" title="Aplicar config template">
          📋 Aplicar Template
        </Btn>

        <ResultSummary summary={summary} />

        {error && (
          <span className="text-noc-err max-w-[40ch] truncate" title={error}>
            ✗ {error}
          </span>
        )}

        <span className="flex-1" />
        <Btn onClick={onClear} color="gray" title="Limpar seleção">✕ limpar</Btn>
      </div>

      {/* OTA picker */}
      {showOtaPicker && (
        <div className="absolute bottom-full left-0 right-0 mb-1 bg-noc-panel border border-noc-border rounded shadow-xl p-2 max-h-64 overflow-y-auto">
          <div className="text-[10px] text-noc-muted mb-1">Selecione release de firmware:</div>
          {releases.length === 0 && <div className="text-noc-muted text-[10px]">Sem releases.</div>}
          {releases.map((r) => (
            <button
              key={r.id}
              type="button"
              onClick={() => runBulkOta(r.id)}
              className="w-full text-left px-2 py-1 hover:bg-noc-bg/50 rounded text-[11px] font-mono"
            >
              v{r.version}
              {r.deprecated && <span className="text-noc-warn ml-2">deprecated</span>}
              <span className="text-noc-muted/60 ml-2 text-[9px]">
                {r.sha256?.slice(0, 8)}…
              </span>
            </button>
          ))}
          <button type="button" onClick={() => setShowOtaPicker(false)} className="mt-1 text-[10px] text-noc-muted hover:text-noc-text">cancelar</button>
        </div>
      )}

      {/* Group picker */}
      {showGroupPicker && (
        <div className="absolute bottom-full left-0 right-0 mb-1 bg-noc-panel border border-noc-border rounded shadow-xl p-2 max-h-64 overflow-y-auto">
          <div className="text-[10px] text-noc-muted mb-1">Selecione grupo:</div>
          <button
            type="button"
            onClick={() => runBulkGroup(null)}
            className="w-full text-left px-2 py-1 hover:bg-noc-bg/50 rounded text-[11px]"
          >
            — sem grupo (desvincular) —
          </button>
          {groups.map((g) => (
            <button
              key={g.id}
              type="button"
              onClick={() => runBulkGroup(g.id)}
              className="w-full text-left px-2 py-1 hover:bg-noc-bg/50 rounded text-[11px]"
            >
              {g.name}
              {g.customer_id && (
                <span className="text-noc-muted/60 text-[9px] ml-2">
                  ({customers.find((c) => c.id === g.customer_id)?.code || "—"})
                </span>
              )}
            </button>
          ))}
          <button type="button" onClick={() => setShowGroupPicker(false)} className="mt-1 text-[10px] text-noc-muted hover:text-noc-text">cancelar</button>
        </div>
      )}

      {/* Tag picker */}
      {showTagPicker && (
        <div className="absolute bottom-full left-0 right-0 mb-1 bg-noc-panel border border-noc-border rounded shadow-xl p-2 max-h-64 overflow-y-auto">
          <div className="text-[10px] text-noc-muted mb-1">Adicionar tag aos devices selecionados:</div>
          {tags.length === 0 && <div className="text-noc-muted text-[10px]">Sem tags. Crie em Manage.</div>}
          {tags.map((t) => (
            <button
              key={t.id}
              type="button"
              onClick={() => runBulkAddTag(t.id)}
              className="w-full text-left px-2 py-1 hover:bg-noc-bg/50 rounded text-[11px] flex items-center gap-2"
            >
              <span className="inline-block w-2 h-2 rounded-full" style={{ backgroundColor: t.color }} />
              {t.name}
            </button>
          ))}
          <button type="button" onClick={() => setShowTagPicker(false)} className="mt-1 text-[10px] text-noc-muted hover:text-noc-text">cancelar</button>
        </div>
      )}

      {/* Sprint 4.1.2 — Modal Reiniciar Relé bulk (mesmo layout Device Details) */}
      <BulkRelayRebootModal
        open={showRelayRebootModal}
        deviceIds={ids}
        breakdown={sel}
        onClose={() => setShowRelayRebootModal(false)}
        onDone={(s) => {
          setSummary(s);
          if (onAfterAction) onAfterAction();
        }}
      />

      {/* Sprint 4.6 — Template picker */}
      {showTemplatePicker && (
        <div className="absolute bottom-full left-0 right-0 mb-1 bg-noc-panel border border-noc-border rounded shadow-xl p-2 max-h-64 overflow-y-auto">
          <div className="text-[10px] text-noc-muted mb-1">
            Selecione template para aplicar nos {ids.length} device(s):
          </div>
          {templatesList.length === 0 && (
            <div className="text-noc-muted text-[10px] italic">
              Nenhum template habilitado. Crie em Templates.
            </div>
          )}
          {templatesList.map((t) => (
            <button
              key={t.id}
              type="button"
              onClick={() => onPickTemplate(t)}
              className="w-full text-left px-2 py-1 hover:bg-noc-bg/50 rounded text-[11px]"
            >
              <span className="font-semibold text-noc-accent">{t.name}</span>
              <span className="text-noc-muted ml-2 text-[10px]">[{t.scope}]</span>
              <span className="text-noc-muted/60 ml-2 text-[10px] font-mono">
                {Object.keys(t.payload_json || {}).map(x => x.replace("config_", "")).join(", ")}
              </span>
            </button>
          ))}
          <button type="button" onClick={() => setShowTemplatePicker(false)}
            className="mt-1 text-[10px] text-noc-muted hover:text-noc-text">
            cancelar
          </button>
        </div>
      )}

      {/* Sprint 4.6 — Apply modal */}
      <TemplateApplyModal
        open={!!applyTemplate}
        template={applyTemplate}
        initialDeviceIds={ids}
        onClose={() => setApplyTemplate(null)}
        onApplied={() => {
          if (onAfterAction) onAfterAction();
        }}
      />
    </div>
  );
}
